// @flow
import {
  call,
  takeEvery,
  put,
  select,
  race,
} from 'redux-saga/effects';
import { normalize } from 'normalizr';
import { delay } from 'redux-saga';
import i18n from 'i18n-js';

import * as types from '../types/monitors';
import * as selectors from '../reducers';
import { REQUEST_TIMEOUT } from '../settings';
import { throwTimeout } from '../lib/http';
import * as monitorsActions from '../actions/monitors';
import * as routesActions from '../actions/routes';
import * as closeableActions from '../actions/closeable';
import { Monitors } from '../api';
import { arrayOfRoutes } from '../api/schemas/routes';
import * as temporalBannersActions from '../actions/temporalBanners';
import * as messages from '../utils/errorDefaults';
import HTTP_STATUS from '../lib/HTTPStatus';


function* fetchMonitor(action) {
  const token = yield select(selectors.getToken);
  const id = action.payload;

  try {
    // Fetch the next route
    const { monitorResponse, timeout } = yield race({
      monitorResponse: call(
        [Monitors, 'detail'],
        { id, token },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchMonitor saga');
    }

    const muser = monitorResponse.user;

    yield put(monitorsActions.fetchMonitorSucceed(
      monitorResponse.id,
      muser.first_name,
      muser.last_name,
      muser.phone,
      muser.profile_picture,
    ));
  } catch (error) {
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;

    yield put(monitorsActions.fetchMonitorFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
      object_id: id,
    }));
  }
}


function* fetchMonitorRoutes(action) {
  const token = yield select(selectors.getToken);
  const id = action.payload;

  try {
    // Fetch the next route
    const { monitorRoutesResponse, timeout } = yield race({
      monitorRoutesResponse: call(
        [Monitors.custom, 'routes'],
        { id, token },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchMonitor saga');
    }
    const {
      entities,
      result,
    } = normalize(monitorRoutesResponse, arrayOfRoutes);

    yield put(monitorsActions.fetchMonitorRoutesSucceed(
      entities.routes,
      result,
    ));
    yield put(monitorsActions.addMonitorRoutes(
      id,
      result,
    ));
  } catch (error) {
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;

    yield put(monitorsActions.fetchMonitorRoutesFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
      object_id: id,
    }));
  }
}

function* addMonitorRoute(action) {
  const token = yield select(selectors.getToken);
  const { id, idRoute } = action.payload;
  const monitor = yield select(selectors.getMonitor, id);
  const oldRoutes = monitor.routes ? monitor.routes : [];
  try {
    // Fetch the next route
    const { monitorRoutesResponse, timeout } = yield race({
      monitorRoutesResponse: call(
        [Monitors.custom, 'addRoute'],
        { id, token, data: { route: idRoute } },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchMonitor saga');
    }
    if (oldRoutes.filter(elm => elm === monitorRoutesResponse.id).length === 0) {
      oldRoutes.push(monitorRoutesResponse.id);
    }
    yield put(routesActions.fetchRouteSucceed(monitorRoutesResponse));
    yield put(monitorsActions.addMonitorRouteSuccess(id, oldRoutes));
    yield put(closeableActions.close('confirmMonitorChange'));
  } catch (error) {
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;

    yield put(monitorsActions.fetchMonitorRoutesFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
      object_id: id,
    }));
  }
}

function* fetchMonitorDayRoutes(action) {
  const token = yield select(selectors.getToken);
  const id = action.payload;

  try {
    // Fetch the next route
    const { monitorDayRoutesResponse, timeout } = yield race({
      monitorRoutesResponse: call(
        [Monitors.custom, 'dayRoutes'],
        { id, token },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('fetchMonitor saga');
    }
    const {
      entities,
      result,
    } = normalize(monitorDayRoutesResponse, arrayOfRoutes);

    yield put(monitorsActions.fetchMonitorRoutesSucceed(
      entities,
      result,
    ));
    yield put(monitorsActions.addMonitorDayRoutes(
      id,
      result,
    ));
  } catch (error) {
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;

    yield put(monitorsActions.fetchMonitorRoutesFailed({
      statusCode,
      message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
      object_id: id,
    }));
  }
}

function* updateMonitor(action) {
  const token = yield select(selectors.getToken);
  const { id } = action.payload;
  try {
    // Update  route
    const { monitorResponse, timeout } = yield race({
      monitorResponse: call(
        [Monitors, 'update'],
        {
          token,
          id,
          data: {
            ...action.payload,
            profile_picture: undefined,
          },
        },
      ),
      timeout: call(delay, REQUEST_TIMEOUT),
    });

    if (timeout) {
      throwTimeout('updateMonitor saga');
    }
    yield put(temporalBannersActions.shout('Estudiante actualizado con exito', 3000, 1, {}));
    yield put(monitorsActions.updateMonitorSuccess({
      ...monitorResponse.user,
      id: monitorResponse.id,
    }));
  } catch (error) {
    // Set error
    const {
      statusCode,
      message,
      data,
      isPlain,
    } = error;
    yield put(temporalBannersActions.shout(messages.UPDATE_ERROR(i18n.t('common.user', { count: 1 })), messages.DEFAULT_DURATION, -1, {}));
    // yield put(userActions.updateUserSucceed(oldUser));
    yield put(monitorsActions.updateMonitorFailed({
      statusCode,
      message: statusCode === HTTP_STATUS.NOT_FOUND ? 'Error al update de user' : message,
      data: isPlain ? 'Error en el servidor' : data,
      retryAction: action,
    }));
  }
}


export function* watchMonitorFetch(): Iterator<any> {
  yield takeEvery(
    types.MONITOR_FETCH_STARTED,
    fetchMonitor,
  );
}

export function* watchFetchMonitorRoutes(): Iterator<any> {
  yield takeEvery(
    types.MONITOR_ROUTES_FETCH_STARTED,
    fetchMonitorRoutes,
  );
}

export function* watchAddMonitorRoutes(): Iterator<any> {
  yield takeEvery(
    types.ADD_MONITOR_ROUTE_STARTED,
    addMonitorRoute,
  );
}

export function* watchUpdateMonitor(): Iterator<any> {
  yield takeEvery(
    types.UPDATE_MONITOR_STARTED,
    updateMonitor,
  );
}



// WEBPACK FOOTER //
// ./src/scripts/sagas/monitors.js